import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/pages/CartPage.css";
import CheckoutPage from "./CheckoutPage";
import { useCart } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import { fetchSavedForLater } from "../services/saveForLaterService";
import { SaveForLaterItem } from "../types/store";
import { formatCurrency } from "../utils/currency";
import { resolveProductImageUrl } from "../utils/productImageUrl";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import useBodyScrollLock from "../hooks/useBodyScrollLock";

const buildQuantityOptions = (stockQuantity: number, currentQuantity: number) => {
  const maxQuantity = stockQuantity > 0 ? Math.min(stockQuantity, 10) : Math.max(currentQuantity, 1);
  return Array.from({ length: maxQuantity }, (_, index) => index + 1);
};

const resolveQuantityValue = (stockQuantity: number, currentQuantity: number) => {
  const maxQuantity = stockQuantity > 0 ? Math.min(stockQuantity, 10) : Math.max(currentQuantity, 1);
  return Math.min(currentQuantity, maxQuantity);
};

type CartModalState =
  | {
      open: false;
      productId: null;
    }
  | {
      open: true;
      productId: number;
    };

const CartPage: React.FC = () => {
  const { items, removeFromCart, updateQuantity } = useCart();
  const { addToWishlist } = useWishlist();
  const [savedItems, setSavedItems] = useState<SaveForLaterItem[]>([]);
  const [cartModal, setCartModal] = useState<CartModalState>({ open: false, productId: null });
  useBodyScrollLock(cartModal.open);

  const getDiscountPercentage = (price: number, originalPrice?: number) => {
    if (!originalPrice || originalPrice <= price) {
      return 0;
    }

    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  const renderCartItem = (
    item:
      | {
          product: {
            id: number;
            brand: string;
            name: string;
            images: string[];
            price: number;
            originalPrice: number;
            stockQuantity: number;
          };
          quantity: number;
        }
      | SaveForLaterItem,
      options: {
        itemKey: string;
        quantityValue: number;
        quantityOptions: number[];
        onQuantityChange?: (quantity: number) => void;
        quantityDisabled?: boolean;
        statusLabel?: string;
      },
    ) => {
    const product = item.product;
    return (
      <article className="store-card cart-item" key={options.itemKey}>
        {!options.quantityDisabled ? (
          <button
            type="button"
            className="cart-item__close"
            aria-label={`Open actions for ${product.name}`}
            onClick={() => setCartModal({ open: true, productId: product.id })}
          >
            ×
          </button>
        ) : null}
        <div className="cart-item__media">
          <div className="cart-item__image-wrap">
            <img src={resolveProductImageUrl(product.images[0])} alt={product.name} />
          </div>
        </div>
        <div className="cart-item__content">
          <div className="cart-item__details">
            {product.brand?.trim() ? <span className="cart-item__brand">{product.brand}</span> : null}
            <h3 className="cart-item__title">{product.name}</h3>
            <div className="cart-item__status-row">
              {options.statusLabel ? <span className="cart-item__status">{options.statusLabel}</span> : null}
              <div className="cart-item__controls">
                {options.quantityDisabled ? (
                  <div className="cart-item__saved-qty">
                    <span>Saved qty</span>
                    <strong>{options.quantityValue}</strong>
                  </div>
                ) : (
                  <label className="cart-item__quantity-select">
                    <span className="cart-item__quantity-label">Qty</span>
                    <select
                      value={options.quantityValue}
                      disabled={options.quantityDisabled || product.stockQuantity <= 0 || !options.onQuantityChange}
                      onChange={(event) => options.onQuantityChange?.(Number(event.target.value))}
                    >
                      {options.quantityOptions.map((quantity) => (
                        <option key={quantity} value={quantity}>
                          {quantity}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="cart-item__pricing">
            <div className="cart-item__price-row">
              <strong className="cart-item__price">{formatCurrency(product.price)}</strong>
              {product.originalPrice > product.price ? (
                <>
                  <span className="cart-item__original-price">{formatCurrency(product.originalPrice)}</span>
                  <span className="cart-item__off">
                    {getDiscountPercentage(product.price, product.originalPrice)}% Off
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </article>
    );
  };

  const activeCartProduct = cartModal.open
    ? items.find((item) => item.product.id === cartModal.productId) ?? null
    : null;

  const loadSavedItems = async () => {
    try {
      const response = await fetchSavedForLater();
      setSavedItems(response.items);
    } catch {
      setSavedItems([]);
    }
  };

  useEffect(() => {
    void loadSavedItems();
  }, []);

  const handleAddToWishlist = async (productId: number) => {
    const cartItem = items.find((item) => item.product.id === productId);
    if (!cartItem) {
      return;
    }

    void addToWishlist(cartItem.product);
    void removeFromCart(productId);
    void loadSavedItems();
  };

  const handleQuantityChange = async (productId: number, quantity: number) => {
    void updateQuantity(productId, quantity);
  };

  const handleRemoveFromCart = async (productId: number) => {
    void removeFromCart(productId);
  };

  if (!items.length && !savedItems.length) {
    return (
      <section className="shell section page-section cart-page">
        <MobilePageHeader title="Cart" />
        <div className="store-card empty-state">
          <h1>Your cart is empty</h1>
          <p>Add products from the catalog to review quantities and pricing.</p>
          <Link className="button" to="/">
            Continue shopping
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="shell section page-section cart-page">
      <MobilePageHeader title="Cart" />
      <div className="cart-layout">
        <div className="cart-items">
          {items.length ? (
            items.map((item) => {
              const quantityValue = resolveQuantityValue(item.product.stockQuantity, item.quantity);
              const quantityOptions = buildQuantityOptions(item.product.stockQuantity, item.quantity);

              return renderCartItem(item, {
                itemKey: String(item.product.id),
                quantityValue,
                quantityOptions,
                onQuantityChange: (quantity) => void handleQuantityChange(item.product.id, quantity),
                statusLabel: item.product.availability === "out-of-stock" ? "Unavailable" : "Ready to ship",
              });
            })
          ) : (
            <div className="store-card empty-state cart-empty-state">
              <span className="cart-empty-state__icon" aria-hidden="true">🛒</span>
              <h2>Your active cart is empty</h2>
              <p>Browse products and tap Add to cart to start building your order.</p>
              <Link className="button" to="/">
                Explore products
              </Link>
            </div>
          )}

          {savedItems.length ? (
            <section className="store-card cart-saved-section">
              <div className="cart-saved-section__heading">
                <div>
                  <span className="eyebrow">Saved for later</span>
                  <h2>Items parked for a future order</h2>
                </div>
                <span className="cart-saved-section__count">{savedItems.length} items</span>
              </div>
              <div className="cart-items">
                {savedItems.map((item) => {
                  const quantityValue = resolveQuantityValue(item.product.stockQuantity, item.quantity);
                  const quantityOptions = buildQuantityOptions(item.product.stockQuantity, item.quantity);

                  return renderCartItem(item, {
                    itemKey: String(item.id),
                    quantityValue,
                    quantityOptions,
                    quantityDisabled: true,
                    statusLabel: "Saved for later",
                  });
                })}
              </div>
            </section>
          ) : null}

          <CheckoutPage embedded />
        </div>

      </div>

      {cartModal.open && activeCartProduct ? (
        <div className="cart-modal" role="presentation">
          <div
            className="cart-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="cart-modal__close"
              onClick={() => setCartModal({ open: false, productId: null })}
              aria-label="Close"
            >
              ×
            </button>
            <div className="cart-modal__content">
              <div className="cart-modal__image">
                <img src={resolveProductImageUrl(activeCartProduct.product.images[0])} alt={activeCartProduct.product.name} />
              </div>
              <div className="cart-modal__body">
                {activeCartProduct.product.brand?.trim() ? (
                  <span className="cart-modal__brand">{activeCartProduct.product.brand}</span>
                ) : null}
                <h3 id="cart-modal-title">{activeCartProduct.product.name}</h3>
                <div className="cart-modal__actions">
                  <button
                    type="button"
                    className="cart-modal__button cart-modal__button--wishlist"
                    onClick={() => {
                      const productId = activeCartProduct.product.id;
                      void handleAddToWishlist(productId);
                      setCartModal({ open: false, productId: null });
                    }}
                  >
                    Add to wishlist
                  </button>
                  <button
                    type="button"
                    className="cart-modal__button cart-modal__button--remove"
                    onClick={() => {
                      const productId = activeCartProduct.product.id;
                      void handleRemoveFromCart(productId);
                      setCartModal({ open: false, productId: null });
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default CartPage;
